import { useState, useRef, useEffect } from "react";
import Icon from "../Icon";
import DatePicker from "../forms/DatePicker";
import MultiSelect from "./MultiSelect";

const inS = { padding: "6px 9px", borderRadius: 8, border: "1px solid var(--border)",
  fontSize: 12, fontFamily: "var(--font)", background: "var(--surface)", color: "var(--text)" };

// ── Panel de configuracion ───────────────────────────────────────────────────
// Aqui el usuario decide como quiere filtrar: que controles ve, con cuales
// arranca la pagina y si se le recuerda lo ultimo que puso.
function PanelConfig({ campos, prefs, onCerrar }) {
  const ref = useRef(null);
  useEffect(() => {
    function fuera(e) { if (ref.current && !ref.current.contains(e.target)) onCerrar(); }
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [onCerrar]);

  const alternar = (key) => prefs.setVisibles(
    prefs.visibles.includes(key)
      ? prefs.visibles.filter(k => k !== key)
      : campos.filter(c => c.key === key || prefs.visibles.includes(c.key)).map(c => c.key)
  );

  const fila = { display: "flex", alignItems: "center", gap: 8, padding: "6px 14px",
    cursor: "pointer", fontSize: 12.5, color: "var(--text)" };
  const accion = { width: "100%", textAlign: "left", padding: "7px 14px", border: "none",
    background: "transparent", cursor: "pointer", fontSize: 12, fontFamily: "var(--font)",
    color: "var(--text2)", fontWeight: 600 };
  const separador = { borderTop: "1px solid var(--border)", margin: "5px 0" };

  return (
    <div ref={ref} style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 320,
      background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10,
      boxShadow: "var(--shadow-sm)", minWidth: 250, padding: "6px 0" }}>

      <div style={{ padding: "4px 14px 6px", fontSize: 10.5, fontWeight: 800,
        letterSpacing: 0.4, color: "var(--text3)", textTransform: "uppercase" }}>
        Filtros visibles
      </div>
      {campos.map(c => (
        <label key={c.key} style={fila}>
          <input type="checkbox" checked={prefs.visibles.includes(c.key)}
            onChange={() => alternar(c.key)}
            style={{ accentColor: "var(--accent)", width: 13, height: 13 }} />
          {c.icono && <Icon name={c.icono} size={11} color="var(--text3)" />}
          <span style={{ flex: 1 }}>{c.label}</span>
        </label>
      ))}

      <div style={separador} />
      <label style={fila}>
        <input type="checkbox" checked={prefs.recordar}
          onChange={e => prefs.setRecordar(e.target.checked)}
          style={{ accentColor: "var(--accent)", width: 13, height: 13 }} />
        <span style={{ flex: 1 }}>Recordar los ultimos filtros</span>
      </label>

      <div style={separador} />
      <button style={accion} onClick={prefs.guardarPorDefecto}>
        Guardar estos filtros como los de inicio
      </button>
      {prefs.tienePorDefecto && (
        <>
          <button style={accion} onClick={prefs.porDefecto}>Volver a mis filtros de inicio</button>
          <button style={{ ...accion, color: "var(--text3)", fontWeight: 500 }} onClick={prefs.olvidarPorDefecto}>
            Olvidar los filtros de inicio
          </button>
        </>
      )}
      <div style={separador} />
      <button style={{ ...accion, color: "var(--red-text)" }}
        onClick={() => { prefs.restablecer(); onCerrar(); }}>
        Restablecer la configuracion
      </button>
    </div>
  );
}

// ── Barra de filtros ─────────────────────────────────────────────────────────
// Unica barra de filtrado de la aplicacion. `campos` describe QUE se puede
// filtrar; `prefs` (usePrefsFiltros) guarda COMO lo quiere el usuario. Las
// opciones de cada campo se pasan aparte porque unas son constantes y otras
// salen de los datos que hay en pantalla.
export default function BarraFiltros({ campos, prefs, opciones = {}, children, derecha }) {
  const [config, setConfig] = useState(false);
  const { valores, setValor, visibles, hayFiltro, limpiar } = prefs;

  const pinta = (c) => {
    if (c.tipo === "texto") {
      return (
        <div key={c.key} style={{ position: "relative" }}>
          <input aria-label={c.label || c.placeholder} value={valores[c.key] || ""} onChange={e => setValor(c.key, e.target.value)}
            placeholder={c.placeholder || c.label}
            style={{ ...inS, width: c.ancho || 190, paddingLeft: 28 }} />
          <Icon name="search" size={11} color="var(--text3)"
            style={{ position: "absolute", left: 9, top: 8 }} />
        </div>
      );
    }
    // Un si/no. No es un MultiSelect de una opcion: "incluir archivados" no es
    // elegir entre valores, es encender algo, y un desplegable para eso hace que
    // haya que abrirlo para saber si esta puesto.
    if (c.tipo === "conmutador") {
      const on = !!valores[c.key];
      return (
        <button key={c.key} className="btn" onClick={() => setValor(c.key, on ? "" : "1")}
          title={c.title || c.label}
          style={{ ...inS, cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
            fontWeight: 600, whiteSpace: "nowrap",
            borderColor: on ? "var(--accent)" : "var(--border)",
            background: on ? "var(--accent-bg)" : "var(--surface)",
            color: on ? "var(--accent)" : "var(--text2)" }}>
          {/* Solo hay icono cuando esta puesto: un cuadrado vacio al lado de un
              boton que ya cambia de color seria decir lo mismo dos veces, y la
              tabla de iconos no tiene un cuadrado sin marcar que valga. */}
          {on && <Icon name="check2-square" size={11} color="var(--accent)" />}
          {c.label}
        </button>
      );
    }
    if (c.tipo === "rango") {
      return (
        <div key={c.key} style={{ display: "flex", alignItems: "center", gap: 4 }}
          title={c.title || "Solapa con este rango"}>
          <DatePicker value={valores[c.desde]} onChange={v => setValor(c.desde, v)} compact
            nombre="Desde" placeholder="Desde" max={valores[c.hasta]} style={{ width: 126 }} />
          <span style={{ color: "var(--text3)", fontSize: 11 }}>a</span>
          <DatePicker value={valores[c.hasta]} onChange={v => setValor(c.hasta, v)} compact
            nombre="Hasta" placeholder="Hasta" min={valores[c.desde]} refDate={valores[c.desde]}
            style={{ width: 126 }} />
        </div>
      );
    }
    return (
      <MultiSelect key={c.key} label={c.label} icono={c.icono}
        options={opciones[c.key] ?? c.opciones ?? []}
        buscarPlaceholder={c.buscar} sinCoincidencias={c.sinCoincidencias}
        ordenar={c.ordenar}
        selected={valores[c.key] || []} onChange={v => setValor(c.key, v)} />
    );
  };

  const aPintar = campos.filter(c => visibles.includes(c.key));

  return (
    <div className="filters-bar bar-wrap" style={{ display: "flex", gap: 7, alignItems: "center",
      marginBottom: 14, flexWrap: "wrap" }}>

      {aPintar.map(pinta)}

      {hayFiltro && (
        <button className="btn" onClick={limpiar} title="Quitar todos los filtros"
          style={{ ...inS, cursor: "pointer", color: "var(--red-text)", borderColor: "#fca5a5",
            display: "flex", alignItems: "center", gap: 4 }}>
          <Icon name="x-lg" size={10} color="var(--red-text)" />Limpiar
        </button>
      )}

      <div style={{ position: "relative" }}>
        <button aria-label="Configurar el filtrado" className="btn" onClick={() => setConfig(o => !o)} title="Configurar el filtrado"
          style={{ ...inS, cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
            color: "var(--text2)" }}>
          <Icon name="sliders" size={11} color="var(--text3)" />
        </button>
        {config && <PanelConfig campos={campos} prefs={prefs} onCerrar={() => setConfig(false)} />}
      </div>

      {children}

      {derecha && (
        <div data-bar-end style={{ marginLeft: "auto", display: "flex", gap: 7, alignItems: "center" }}>
          {derecha}
        </div>
      )}
    </div>
  );
}
